import type { TimeEntryItem } from '@hygge/shared';
import { formatEntryDuration } from './entry-duration';
import { viewerTimeZone } from '@/lib/timezone';
import { entryClock, entryEndWall, entryStartWall, formatEntryDate } from './wall-clock';

/**
 * What the confirmation asks before a time entry is destroyed.
 *
 * Two controls delete an entry — the hover icon on a timesheet row and the
 * trash button inside the Edit dialog — and both used to fire the mutation
 * straight from the click handler. Now both ask, and they ask in the same
 * words: the copy lives here rather than in either component so neither can
 * drift, and so the wording is testable in a runner with no DOM. [HW-843]
 */

/** The fields the copy reads. Narrow, so a test can build one by hand. */
type DescribableEntry = Pick<
  TimeEntryItem,
  'description' | 'duration' | 'startedAt' | 'endedAt' | 'timezone' | 'task' | 'project'
>;

/** How much of a long description the question carries before cutting it. */
const MAX_LABEL_LENGTH = 60;

/**
 * What to call the entry in the question.
 *
 * The same fallback chain the timesheet row prints as its label — description,
 * then the task's title, then the project — so the dialog names the row the
 * click landed on rather than some other rendering of it. That is the whole
 * point on the riskier path: the icon sits beside a clickable row, and a
 * misclick has to be recognisable as one.
 *
 * One deliberate difference: whitespace does not count as a description here,
 * where the row would print it and look blank. A dialog headed `Delete “”?`
 * names nothing, and the task or project behind it always does.
 */
export function entryDeleteLabel(entry: DescribableEntry): string {
  const label = entry.description?.trim() || entry.task?.title.trim() || entry.project.name;
  // By code point, not by UTF-16 unit: `slice` through an emoji leaves a lone
  // surrogate, which renders as the replacement character in the title.
  const points = Array.from(label);
  return points.length > MAX_LABEL_LENGTH
    ? `${points.slice(0, MAX_LABEL_LENGTH).join('').trimEnd()}…`
    : label;
}

/**
 * The line under the question: how much time, on what, and when.
 *
 * The author's clock and day, read off the stored UTC digits — the reading the
 * timesheet groups and labels its rows by [HW-858] [HW-916] — or, on a legacy
 * row the backfill has not converted yet, its instant on the viewer's clock,
 * which is what that row prints. A confirmation that named a different day
 * than the row it was opened from would be worse than none.
 */
export function entryDeleteSummary(entry: DescribableEntry): string {
  const where = entry.task ? `${entry.project.name} / ${entry.task.key}` : entry.project.name;
  return `${formatEntryDuration(entry.duration)} · ${where} · ${formatEntryWhen(entry)}`;
}

/** "Tue, Sep 1, 09:00 — 11:30", or without the second half on a bare entry. */
function formatEntryWhen(entry: DescribableEntry): string {
  // One zone for both ends, so a legacy row's two clocks cannot be read in two. [HW-916]
  const viewerZone = viewerTimeZone();
  const startWall = entryStartWall(entry, viewerZone);
  const day = formatEntryDate(startWall, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const from = entryClock(startWall);
  // `endedAt` is null on an entry no stop ever closed — a range with one end is
  // not a range, so the line simply stops at the time the work started.
  const endWall = entryEndWall(entry, viewerZone);
  const to = endWall ? entryClock(endWall) : null;
  return to ? `${day}, ${from} — ${to}` : `${day}, ${from}`;
}
